import type { TransformationTestResponse } from '../services/api';
import { apiService } from '../services/api';

export const hasPreviewRows = (response?: TransformationTestResponse | null): boolean => {
  if (!response) {
    return false;
  }
  return Object.values(response.sample_output || {}).some(
    (rows) => Array.isArray(rows) && rows.length > 0
  );
};

export interface PreviewLoadResult {
  response: TransformationTestResponse;
  notice?: string | null;
  mode: 'distributed' | 'local';
}

const normaliseTargetTables = (tables?: string[] | null): string[] | undefined => {
  if (!tables || tables.length === 0) {
    return undefined;
  }
  const seen = new Set<string>();
  const cleaned: string[] = [];
  tables
    .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
    .filter((entry) => entry.length > 0)
    .forEach((entry) => {
      const key = entry.toLowerCase();
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      cleaned.push(entry);
    });
  return cleaned.length ? cleaned : undefined;
};

export const loadPreviewSamples = async (
  batchId: string,
  recipeId: string,
  limit: number = 50,
  targetTables?: string[],
  recipe?: Record<string, unknown>
): Promise<PreviewLoadResult> => {
  const tableFilters = normaliseTargetTables(targetTables);
  let distributedResponse: TransformationTestResponse | null = null;
  let distributedError: unknown = null;

  try {
    if (recipe) {
      // V2 Ephemeral Preview
      const jobId = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `job-${Date.now()}`;
      distributedResponse = await apiService.runPreviewJob(batchId, jobId, {
        recipe,
        sample_size: limit,
        target_tables: tableFilters
      });
    } else {
      // V1 Stored Preview
      distributedResponse = await apiService.getDistributedPreview(batchId, recipeId, limit, tableFilters);
    }
    
    if (hasPreviewRows(distributedResponse)) {
      return { response: distributedResponse, notice: null, mode: 'distributed' };
    }
  } catch (error) {
    distributedError = error;
    console.warn('Distributed preview failed; attempting local fallback.', error);
  }

  try {
    const fallbackResponse = await apiService.getTransformPreview(batchId, recipeId, limit, tableFilters);
    if (hasPreviewRows(fallbackResponse)) {
      let notice: string | null = null;
      if (distributedResponse && !hasPreviewRows(distributedResponse)) {
        notice = 'Distributed preview returned no rows. Showing local preview instead.';
      } else if (distributedError) {
        notice = 'Distributed preview is unavailable. Showing local preview output.';
      }
      return { response: fallbackResponse, notice, mode: 'local' };
    }
  } catch (fallbackError) {
    const message = fallbackError instanceof Error ? fallbackError.message : 'Failed to load preview.';
    throw new Error(message);
  }

  if (distributedError && !distributedResponse) {
    throw new Error('Preview unavailable: distributed execution failed and no local rows were returned.');
  }

  throw new Error('Preview completed but no rows were returned. Check mapping expressions or try regenerating.');
};
